import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Row, Col, ListGroup, Image, Card, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import CheckoutSteps from "../components/CheckoutSteps";
import Message from "../components/Message";
import Loader from "../components/Loader";
import { useCreateOrderMutation, useInitiateRazorPayOrderMutation } from "../slices/ordersApiSlice";
import { useSendOrderEmailMutation } from "../slices/emailApiSlice";
//import { useSendSmsMutation } from "../slices/smsApiSlice";
import { useSendWhatsAppMessageMutation } from "../slices/whatsAppApiSlice";
import { toggleNoFeeShippingTax, clearCartItems } from "../slices/cartSlice";
import { RAZORPAY_URL } from "../constants";
import flavchap_tpv_logo from '../assets/flavchap_tpv_logo.png';
import razorpay_logo from '../assets/razorpay_logo.png';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';

const PlaceOrderScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);

  const [initiateRazorPayOrder] = useInitiateRazorPayOrderMutation();
  const [sendOrderEmail] = useSendOrderEmailMutation();
  //const [sendSms, { isLoading: isLoadingSendSms, isSuccess: isSuccessSendSms, isError: isErrorSendSms }] = useSendSmsMutation();
  const [sendWhatsAppMessage] = useSendWhatsAppMessageMutation();
  const [createOrder, { isLoading, errorCreateOrder }] = useCreateOrderMutation();
  const { userInfo } = useSelector((state) => state.auth);

  // UI states
  const [isPaymentLoading, setIsPaymentLoading] = useState(false); // Spinner for RazorPay button
  const [showOrderModal, setShowOrderModal] = useState(false);     // Modal after payment success

  useEffect(() => {
    if (!cart.shippingAddress.addressLine1) {
      navigate('/shipping');
    }
  }, [cart.shippingAddress.addressLine1, navigate]);

  const placeOrderHandler = async () => {
    try {
      setIsPaymentLoading(true); // Show spinner inside button

      const loaded = await loadScript(RAZORPAY_URL);
      if (!loaded) {
        setIsPaymentLoading(false);
        toast.error('RazorPay failed to load');
        return;
      }

      const initResponse = await initiateRazorPayOrder({
        orderItems: cart.cartItems,
        shippingAddress: cart.shippingAddress,
        paymentMethod: cart.paymentMethod,
        noFeeShippingTax: cart.noFeeShippingTax
      }).unwrap();

      const {
        razorPayOrder: { id: razorPayOrderId, amount, currency },
        prices,
        validatedOrderItems,
      } = initResponse;

      const razorPayKeyId = process.env.REACT_APP_RAZORPAY_KEY_ID;
      const businessName = process.env.REACT_APP_BUSINESS_NAME;
      const razorPayThemeColor = process.env.REACT_APP_RAZORPAY_THEME_COLOR;

      const options = {
        key: razorPayKeyId,
        amount,
        currency,
        name: businessName,
        description: `Payment for order ${razorPayOrderId}`,
        image: flavchap_tpv_logo,
        order_id: razorPayOrderId,
        handler: async function (response) {
          const paymentDetails = {
            razorPayOrderId,
            razorPayPaymentId: response.razorpay_payment_id,
            razorPaySignature: response.razorpay_signature,
          };

          try {
            // Show modal while order is being created
            setShowOrderModal(true);

            const finalOrder = await createOrder({
              orderItems: validatedOrderItems,
              shippingAddress: cart.shippingAddress,
              itemsPrice: prices.itemsPrice,
              taxPrice: prices.taxPrice,
              platformFee: prices.platformFee,
              platformFeeTax: prices.platformFeeTax,
              taxAndFees: prices.taxAndFees,
              shippingPrice: prices.shippingPrice,
              shippingTax: prices.shippingTax,
              totalPrice: prices.totalPrice,
              paymentDetails,
            }).unwrap();

            errorCreateOrder && toast.error(errorCreateOrder, {autoClose: 2000});

            await sendOrderEmail(finalOrder);
            //console.log('Phone is ' + cart.shippingAddress.phone);
            //console.log('Final order is ' + finalOrder);
            //await sendSms({phone: cart.shippingAddress.phone, message: finalOrder});
            try {
              await sendWhatsAppMessage({phone: userInfo.phone, message: finalOrder});
              //console.log("WhatsApp Message succesfully sent");
            } catch (error) {
              console.log(error);
            }

            toast.success('Payment successful & Order placed', {autoClose: 1000});
            dispatch(clearCartItems());

            // Google Ads conversion tracking
            if (window.gtag) {
              window.gtag('event', 'conversion', {
                send_to: 'AW-17715005844/lWMzCL6OvL8bEJSTlv9B',
                value: prices.totalPrice,
                currency: 'INR',
                transaction_id: razorPayOrderId,
              });
            }

            // Hide modal before navigating
            setShowOrderModal(false);
            navigate(`/order/${finalOrder._id}`);
          } catch (err) {
            setShowOrderModal(false);
            toast.error(err?.data?.message || 'Payment verification failed', {autoClose: 2000});
          }
        },
        prefill: {
          name: userInfo.name,
          email: userInfo.email,
          contact: userInfo.phone
        },
        theme: {
          color: razorPayThemeColor
        }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

      paymentObject.on("payment.failed", function () {
        setIsPaymentLoading(false);
        toast.error("Payment failed or cancelled.");
      });

      // Reset button spinner once RazorPay opens
      setIsPaymentLoading(false);
    } catch (error) {
      setIsPaymentLoading(false);
      toast.error(error?.data?.message || error.message);
    }
  };

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => reject(false);
      document.body.appendChild(script);
    });
  };

  return (
    <>
      <CheckoutSteps step1 step2 step3 />
      <Row>
        <Col md={8}>
          {/* Left Column with Shipping & Items */}
          <ListGroup variant="flush">
            <ListGroup.Item>
              <h2>Shipping</h2>
              <p>
                <strong>Address: </strong>
                {cart.shippingAddress.name}, {cart.shippingAddress.addressLine1},{" "}
                {cart.shippingAddress.addressLine2 ? cart.shippingAddress.addressLine2 + "," : ""}{" "}
                {cart.shippingAddress.city}, {cart.shippingAddress.state},{" "}
                {cart.shippingAddress.postalCode}, {cart.shippingAddress.phone}
              </p>
            </ListGroup.Item>
            <ListGroup.Item>
              <h2>Order Items</h2>
              {cart.cartItems.length === 0 ? (
                <Message>Your cart is empty</Message>
              ) : (
                <ListGroup>
                  {cart.cartItems.map((item, index) => (
                    <ListGroup.Item key={index}>
                      <Row>
                        <Col xs={6} sm={6} md={2}>
                          <Image src={item.image} alt={item.name} fluid rounded />
                        </Col>
                        <Col xs={6} sm={6} md={8}>
                          <Row>
                            <span>
                              <Link to={`/product/${item._id}`}>{item.name}</Link>
                              <br />
                              {(item.subCategory === "Hot Snacks" &&
                                "Spice Level: " + item.selectedSpiceLevel) ||
                                (item.subCategory === "Sweets" &&
                                  "Sweetness: " + item.selectedSweetness)}
                            </span>
                          </Row>
                          <Row>
                            <span>
                              Weight: {item.customization.priceCustomization[item.selectedPriceCustomization].weight}
                            </span>
                          </Row>
                          <Row>
                            <span>
                              Price: {item.quantity} x ₹
                              {item.customization.priceCustomization[item.selectedPriceCustomization].price} =
                              ₹
                              {item.quantity *
                                item.customization.priceCustomization[item.selectedPriceCustomization].price}
                            </span>
                          </Row>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              )}
            </ListGroup.Item>
            <ListGroup.Item>
              <Message variant="info">
                <strong>Order once placed cannot be cancelled</strong>
              </Message>
            </ListGroup.Item>
          </ListGroup>
        </Col>

        <Col md={4}>
          {/* Right Column with Summary */}
          <Card className="h-auto">
            <ListGroup variant="flush">
              {userInfo?.isAdmin && (
              <ListGroup.Item>
                <label className="d-flex align-items-center gap-2">
                  <input
                    type="checkbox"
                    checked={cart.noFeeShippingTax}
                    onChange={() => dispatch(toggleNoFeeShippingTax())}
                  />
                  <strong>Admin: Free shipping & no tax/fees</strong>
                </label>
              </ListGroup.Item>
              )}
              <ListGroup.Item>
                <h2>Order Summary</h2>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col>Items Price: </Col>
                  <Col>₹{cart.itemsPrice}</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col>Shipping Price: </Col>
                  <Col>₹{cart.shippingPrice}</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col className="d-flex align-items-center gap-1">
                    <span>Tax And Fees:</span>
                    <OverlayTrigger
                      trigger="click"
                      placement="right"
                      rootClose
                      overlay={
                        <Tooltip id="tax-info-tooltip">
                          GST on Items (5%) = ₹{cart.taxPrice}
                          <br />
                          GST on shipping (5%) = ₹{cart.shippingTax}
                          <br />
                          Platform fee = ₹{cart.platformFee}
                          <br />
                          GST on Platform fee (18%) = ₹{cart.platformFeeTax}
                        </Tooltip>
                      }
                    >
                      <span style={{ cursor: "pointer" }}>
                        <FaInfoCircle style={{ color: "#6c757d" }} />
                      </span>
                    </OverlayTrigger>
                  </Col>
                  <Col>₹{cart.taxAndFees}</Col>
                </Row>
              </ListGroup.Item>
              <ListGroup.Item>
                <Row>
                  <Col>
                    <strong>Total Price: </strong>
                  </Col>
                  <Col>
                    <strong>₹{cart.totalPrice}</strong>
                  </Col>
                </Row>
              </ListGroup.Item>
              {/* <ListGroup.Item>
                {error && <Message variant="danger">{error.data.message}</Message>}
              </ListGroup.Item> */}
              <ListGroup.Item>
                <Button
                  type="button"
                  variant="primary"
                  className="btn-block d-flex align-items-center justify-content-center gap-2"
                  disabled={cart.cartItems.length === 0 || isPaymentLoading}
                  onClick={placeOrderHandler}
                >
                  {isPaymentLoading ? (
                    <>
                      <Spinner animation="border" size="sm" /> Processing Payment...
                    </>
                  ) : (
                    <>
                      <strong>Pay ₹{cart.totalPrice} </strong>
                      <img
                        src={razorpay_logo}
                        alt="Razorpay"
                        style={{
                          height: "20px",
                          verticalAlign: "middle",
                        }}
                      />
                      <strong> & Place Order</strong>
                    </>
                  )}
                </Button>
                {isLoading && <Loader />}
              </ListGroup.Item>
            </ListGroup>
          </Card>
        </Col>
      </Row>

      {/* Modal shown while order is being created */}
      <Modal show={showOrderModal} centered backdrop="static" keyboard={false}>
        <Modal.Body className="text-center">
          <Spinner animation="border" className="mb-3" />
          <h5>Order is being created...</h5>
          <p className="text-muted">Please wait while we confirm your order details.</p>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default PlaceOrderScreen;